import {SelectionModel} from '@angular/cdk/collections';
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {fromEvent, merge, of, Subscription} from 'rxjs';
import {debounceTime, delay, distinctUntilChanged, skip, take, tap} from 'rxjs/operators';
// tslint:disable-next-line: max-line-length
import {
    AppendixCategory,
    AppendixCategoryDeleted,
    AppendixCategoriesDataSource,
    AppendixCategoriesPageRequested,
    selectTrashedAppendixCategoriesCount, AppendixCategoriesService, selectAdminTrashedAppendixCategoriesCount,
    AppendixCategoryOnServerRestored, AppendixCategoryOnServerAdminRestored, AppendixCategoryTrashFlushed,
    AppendixCategoryDeletedFromAdminTrash, AppendixCategoryDeletedFromTrash,
} from '../../../../../core/admin';
import {AppState} from '../../../../../core/reducers';
import {LayoutUtilsService, QueryParamsModel} from '../../../../../core/_base/crud';
import {LayoutConfigService, SubheaderService} from '../../../../../core/_base/layout';
import {AppendixCategoriesActionToggleLoading} from '../../../../../core/admin/_actions/appendix-category.actions';
import { User } from 'src/app/core/auth';
import { currentUser } from 'src/app/core/mad-auth/mad-auth.store';
import { SubdomainService } from 'src/app/core/_base/subdomain.service';


@Component({
    selector: 'kt-appendix-categories-list',
    templateUrl: './appendix-categories-list.component.html',
    styleUrls: ['./appendix-categories-list.component.scss']
})

export class AppendixCategoriesListComponent implements OnInit, OnDestroy {

    dataSource: AppendixCategoriesDataSource;
    displayedColumns = ['id', 'name', 'appendix_cnt', 'actions'];
    trashCnt = 0;
    adminTrashCnt = 0;

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild('sort1', {static: true}) sort: MatSort;
    // Filter fields
    @ViewChild('searchInput', {static: true}) searchInput: ElementRef;
    // Selection
    selection = new SelectionModel<AppendixCategory>(true, []);
    appendixCategoryResult: AppendixCategory[] = [];

    // Subscriptions
    currentUser: User|null
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute:
     * @param router:
     * @param dialog: MatDialog
     * @param snackBar: MatSnackBar
     * @param subheaderService:
     * @param layoutUtilsService: LayoutUtilsService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     * @param service:
     * @param layoutConfigService:
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                public dialog: MatDialog,
                public snackBar: MatSnackBar,
                public subheaderService: SubheaderService,
                private layoutUtilsService: LayoutUtilsService,
                private translate: TranslateService,
                private store: Store<AppState>,
                private service: AppendixCategoriesService,
                private layoutConfigService: LayoutConfigService,
                private subdomainService: SubdomainService
            ) {
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit() {
        const userSub = this.store.select(currentUser).subscribe(user => this.currentUser = user)
        this.subscriptions.push(userSub)
        this.paginator._intl.itemsPerPageLabel = this.translate.instant('GENERAL.PAGELABEL');
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadAppendixCategoriesList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);

        // Filtration, bind to searchInput
        const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
            // tslint:disable-next-line:max-line-length
            debounceTime(50), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
            distinctUntilChanged(), // This operator will eliminate duplicate values
            tap(() => {
                this.paginator.pageIndex = 0;
                this.loadAppendixCategoriesList();
            })
        )
            .subscribe();
        this.subscriptions.push(searchSubscription);

        // Init DataSource
        this.dataSource = new AppendixCategoriesDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {
            this.appendixCategoryResult = res;
        });
        this.subscriptions.push(entitiesSubscription);

        const selectTrashedSubscription = this.store.pipe(
            select(selectTrashedAppendixCategoriesCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(selectTrashedSubscription);

        const selectAdminTrashedSubscription = this.store.pipe(
            select(selectAdminTrashedAppendixCategoriesCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(selectAdminTrashedSubscription);

        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitation
            this.loadAppendixCategoriesList();
        }); // Remove this line, just loading imitation
    }

    /**
     * Load AppendixCategories List from service through data-source
     */
    loadAppendixCategoriesList() {
        this.selection.clear();
        const queryParams = new QueryParamsModel(
            this.filterConfiguration(),
            this.sort.direction,
            this.sort.active,
            this.paginator.pageIndex + 1,
            this.paginator.pageSize
        );
        // Call request from server
        this.store.dispatch(new AppendixCategoriesPageRequested({page: queryParams}));
        this.selection.clear();
    }

    /**
     * Returns object for filter
     */
    filterConfiguration(): any {
        const filter: any = {};
        const searchText: string = this.searchInput.nativeElement.value;

        filter.name = searchText;
        if (!searchText) {
            return filter;
        }
        return filter;
    }

    /**
     * Returns object for filter
     */
    resetFilter(): any {
        this.searchInput.nativeElement.value = '';
        this.loadAppendixCategoriesList();
    }

    /**
     * Show add appendixCategory dialog
     */
    addAppendixCategory() {
        this.editAppendixCategory(null);
    }

    /**
     * Show Edit appendixCategory view and save after success close result
     * @param _appendixCategory: AppendixCategory
     */
    editAppendixCategory(_appendixCategory) {
        if (_appendixCategory) {
            this.router.navigate(['edit', _appendixCategory.id], {relativeTo: this.activatedRoute});
        } else {
            this.router.navigate(['add'], {relativeTo: this.activatedRoute});
        }
    }

    deleteAppendixCategory(_item: AppendixCategory) {
        const _title = this.translate.instant('APPENDIX_CATEGORY.LIST.DIALOG.DELETE.TITLE');
        const _description: string = this.translate.instant('APPENDIX_CATEGORY.LIST.DIALOG.DELETE.DESCRIPTION', {name: _item.name});
        const _waitDesciption = this.translate.instant('APPENDIX_CATEGORY.LIST.DIALOG.DELETE.WAITY_DESCRIPTION');

        const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }

            this.store.dispatch(new AppendixCategoryDeleted({id: _item.id}));
        });
    }

    reachAppendix(appendixCategory: AppendixCategory) {
        const mainPath = this.layoutConfigService.getCurrentMainRoute();
        const url = `/org/${this.subdomainService.subDomain}/admin-management/appendix-categories/${appendixCategory.id}/appendices`;
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(el => el.unsubscribe());
    }

// Fetch trash data
    trash() {
        this.store.dispatch(new AppendixCategoriesActionToggleLoading({isLoading: true}));
        this.service.getTrashed().subscribe(res => {

            this.store.dispatch(new AppendixCategoriesActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    deletedUser: elem.userDeletedBy,
                    date: elem.user_deleted
                });
            });
            this.show_trash(items);
        });
    }

    // Fetch admin trash data
    adminTrash() {
        this.store.dispatch(new AppendixCategoriesActionToggleLoading({isLoading: true}));
        this.service.getAdminTrashed().subscribe(res => {

            this.store.dispatch(new AppendixCategoriesActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    deletedUser: elem.deletedBy,
                    date: elem.deleted_at
                });
            });
            this.show_trash(items, true);
        });
    }

    /**
     * Show Edit agency dialog and save after success close result
     * @param items: any[]
     * @param isAdmin: boolean
     */
    show_trash(items: any[], isAdmin = false) {
        let _title = this.translate.instant('APPENDIX_CATEGORY.LIST.DIALOG.SHOW_TRASH.TITLE');
        if (isAdmin) {
            _title = this.translate.instant('APPENDIX_CATEGORY.LIST.DIALOG.SHOW_TRASH.TITLE_ADMIN');
        }
        this.layoutUtilsService.trashedEntities(_title, items, this, isAdmin);
    }

    restore(id: number, isAdmin = false) {
        if (isAdmin) {
            this.store.dispatch(new AppendixCategoryOnServerAdminRestored({id}));
        } else {
            this.store.dispatch(new AppendixCategoryOnServerRestored({id}));
        }
    }

    delete(id: number) {
        this.store.dispatch(new AppendixCategoryDeletedFromTrash({id}));
    }

    hardDelete(id: number) {
        this.store.dispatch(new AppendixCategoryDeletedFromAdminTrash({id}));
    }

    flush() {
        this.store.dispatch(new AppendixCategoryTrashFlushed());
    }
}
